import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { intersection, isEmpty } from 'lodash';

import { useStyles } from '~/assets/styles';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SimpleSearchField from '~/components/core/SimpleSearchField';
import ThreeDotsMenu from '~/components/core/ThreeDotsMenu';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import { LobMultiSelect } from '~/components/TPA/LOB/LobMultiselect';
import SubOrganizationMultiSelect from '~/components/TPA/SubOrganizations/SubOrganizationMultiSelect';
import Checkbox from '~/components/ui/atoms/Checkbox';
import Switch from '~/components/ui/atoms/Switch';
import COMMUNICATION_TEMPLATE_ACTION from '~/server_shared/generated-types/COMMUNICATION_TEMPLATE_ACTION';
import { reportAxiosError, subOrgIdToNameDict } from '~/Utils';
import { getLobDescription } from '~/Utils/lobUtils';

import CardDialog from '../../../CardDialog';
import { Heading, SortableTable } from '../../../core';
import PlusIcon from '../../../icons/PlusIcon';
import LoadingIndicator from '../../../LoadingIndicator';
import useOrganization from '../../../OrganizationContext';
import useDataFetcher from '../../../useDataFetcher';
import { useSysconfig } from '../../SystemConfigurationScreen';

import AddOrEditRuleDialog from './AddOrEditRuleDialog';

const AutomaticCommunications = () => {
  const { organization } = useSysconfig();
  const { isTwilioEnabled } = useOrganization();
  const { lobConfigurationsDict } = useLobConfiguration();
  const { classes } = useStyles();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [filteredParams, setFilteredParams] = useState({ subOrgIds: [], lobs: [], channels: [], searchTerm: '' });
  const [ruleToEdit, setRuleToEdit] = useState(null);

  const {
    isLoading: isLoadingRules,
    isError: isErrorRules,
    data: rules,
    reloadData: reloadRules,
  } = useDataFetcher(`/api/v1/organizations/${organization.id}/automatic_communications/rules`);

  const {
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
    data: supportedEvents,
  } = useDataFetcher(`/api/v1/organizations/${organization.id}/automatic_communications/supported_events`);

  const isLoading = isLoadingRules || isLoadingEvents;
  const isError = isErrorRules || isErrorEvents;

  const handleSubOrgChanged = (subOrgIds) => {
    setFilteredParams((params) => ({ ...params, subOrgIds }));
  };

  const handleLobsChanged = (lobs) => {
    setFilteredParams((params) => ({ ...params, lobs }));
  };

  const handleChannelChanged = ({ target: { value: channels } }) => {
    setFilteredParams((params) => ({ ...params, channels }));
  };

  const handleSearchTermChanged = (searchTerm) => {
    setFilteredParams((params) => ({ ...params, searchTerm }));
  };

  const handleSubmitRule = async (values) => {
    const baseUrl = `/api/v1/organizations/${organization.id}/automatic_communications/rules`;
    if (values.id) {
      await callAPI('put', `${baseUrl}/${values.id}`, values);
    } else {
      await callAPI('post', baseUrl, values);
    }
  };

  const handleCloseRuleDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  const handAddRuleClicked = () => {
    setIsRuleDialogOpen(true);
  };

  const filteredRules = useMemo(() => {
    let filteredRules = rules || [];

    if (rules) {
      if (!isEmpty(filteredParams.subOrgIds)) {
        filteredRules = filteredRules.filter(
          (rule) => rule.is_all_sub_orgs || !isEmpty(intersection(rule.sub_organization_ids, filteredParams.subOrgIds))
        );
      }

      if (!isEmpty(filteredParams.lobs)) {
        filteredRules = filteredRules.filter((rule) => {
          if (rule.lobs.length === 0) {
            return true;
          }
          return intersection(filteredParams.lobs, rule.lobs).length > 0;
        });
      }

      if (!isEmpty(filteredParams.channels)) {
        filteredRules = filteredRules.filter((rule) => filteredParams.channels.includes(rule.channel));
      }

      if (filteredParams.searchTerm) {
        filteredRules = filteredRules.filter((rule) =>
          rule.display_name.toLowerCase().includes(filteredParams.searchTerm.toLowerCase())
        );
      }
    }

    return filteredRules;
  }, [filteredParams.lobs, filteredParams.searchTerm, filteredParams.subOrgIds, filteredParams.channels, rules]);

  const callAPI = async (method, url, values) => {
    try {
      setIsProcessing(true);
      await axios({ method, url, data: values });
      await reloadRules();
    } catch (error) {
      await reportAxiosError(error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteRule = async (rule) => {
    await callAPI('delete', `/api/v1/organizations/${organization.id}/automatic_communications/rules/${rule.id}`);
  };

  const handEditRuleClicked = (rule) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  const handleEnabledChanged = async (rule) => {
    await callAPI('post', `/api/v1/organizations/${organization.id}/automatic_communications/rules/${rule.id}/enable`, {
      is_enabled: !rule.is_enabled,
    });
  };

  const actionOptions = (rule) => [
    {
      onClick: () => handEditRuleClicked(rule),
      node: 'Edit',
      key: 'editRule',
    },
    {
      onClick: () => handleDeleteRule(rule),
      withConfirmProps: {
        title: 'Delete Rule?',
        contentText: `Are you sure you want to delete "${rule.display_name}"?`,
        primaryButtonName: 'Delete',
      },
      node: 'Delete',
      key: 'deleteRule',
    },
  ];

  const enabledCell = (rule) => (
    <Switch onChange={() => handleEnabledChanged(rule)} disabled={isProcessing} checked={rule.is_enabled} />
  );

  const actionsCell = (rule) => (
    <div>
      <ThreeDotsMenu options={actionOptions(rule)} />
    </div>
  );

  const columns = [
    { id: 'display_name', label: 'Rule Name' },
    {
      id: 'lob',
      label: 'LOB',
      specialCell: (rule) =>
        rule.is_all_lobs
          ? 'All'
          : rule.lobs.map((lobKey) => getLobDescription(lobKey, lobConfigurationsDict)).join(', '),
    },
    { id: 'context', label: 'Level' },
    {
      id: 'recipient',
      label: 'Recipient',
      specialCell: (rule) => COMMUNICATION_TEMPLATE_ACTION.RECIPIENT[rule.recipient],
    },
    {
      id: 'enable',
      numeric: false,
      label: 'Enable',
      specialCell: enabledCell,
      disableSort: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      width: '90px',
      specialCell: actionsCell,
    },
  ];

  if (organization.sub_organizations_enabled) {
    columns.splice(1, 0, {
      id: 'sub_organization_ids',
      label: 'Sub Organizations',
      specialCell: (rule) => {
        let res;
        const subOrgIdToName = subOrgIdToNameDict(organization);
        if (rule.is_all_sub_orgs) {
          res = Object.values(subOrgIdToName);
        } else {
          res = rule.sub_organization_ids.map((id) => subOrgIdToName[id]);
        }

        return res.join(', ');
      },
    });
  }

  if (isLoading || isError) {
    return <LoadingIndicator isError={isError} />;
  }

  const onSubmit = async (values) => {
    await handleSubmitRule(values);
    handleCloseRuleDialog();
  };

  const availableChannels = Object.keys(COMMUNICATION_TEMPLATE_ACTION.CHANNEL).filter((channelKey) => {
    // Only show SMS if Twilio is enabled
    if (channelKey === 'SMS') {
      return isTwilioEnabled;
    }
    return true;
  });

  return (
    <CardDialog noCardTitle>
      <Grid container className="p-20">
        <Grid item xs={12} className="pb-12">
          <Heading variant={Heading.TYPES.H3}>Communications Rules</Heading>
        </Grid>
        <Grid item xs={12} className="inline-flex justify-end">
          <Button
            color="primary"
            onClick={handAddRuleClicked}
            startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
          >
            Add Rule
          </Button>
        </Grid>
        <Grid container spacing={2} className="inline-flex items-baseline pb-40">
          <Grid item xs={3} className="inline-flex">
            <SimpleSearchField
              label="Search Rule"
              placeholder="Search"
              onChange={handleSearchTermChanged}
              defaultValue=""
            />
          </Grid>
          {organization.sub_organizations_enabled && (
            <Grid item xs={3} className="inline-flex">
              <SubOrganizationMultiSelect onChange={handleSubOrgChanged} />
            </Grid>
          )}
          <Grid item xs={3} className="inline-flex">
            <LobMultiSelect subOrganizationIds={filteredParams.subOrgIds} onChange={handleLobsChanged} />
          </Grid>
          <Grid item xs={3} className="inline-flex">
            <TextField
              value={filteredParams.channels}
              label="Types"
              className={classes.textField}
              fullWidth
              select
              onChange={handleChannelChanged}
              SelectProps={{
                multiple: true,
                renderValue: (values) => values.map((value) => COMMUNICATION_TEMPLATE_ACTION.CHANNEL[value]).join(', '),
              }}
            >
              {availableChannels.map((channelKey) => (
                <MenuItem key={channelKey} value={channelKey}>
                  <Checkbox checked={filteredParams.channels.includes(channelKey)} />
                  {COMMUNICATION_TEMPLATE_ACTION.CHANNEL[channelKey]}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SortableTable columns={columns} rows={filteredRules} disableSortByUser keepRowsOrder />
        </Grid>
      </Grid>
      {isRuleDialogOpen ? (
        <AddOrEditRuleDialog
          supportedEvents={supportedEvents}
          onCancel={handleCloseRuleDialog}
          onSubmit={onSubmit}
          rule={ruleToEdit}
        />
      ) : null}
    </CardDialog>
  );
};

export default AutomaticCommunications;
